// client/src/utils/geofence.js
//
// Deciding whether someone is inside the school grounds.
//
// THE BUG THIS EXISTS TO FIX: both clients read `coords.accuracy`, displayed it
// to the teacher, and then threw it away when deciding. The check was
// `distance <= radius` against a raw fix that, on a cold start indoors, is
// routinely ±500m or worse — it comes from wifi and cell towers, not GPS. So a
// teacher standing in the staffroom was told "you are 780m from school" and
// blocked, while the panel above helpfully printed "GPS ±1200m". The number
// that proved the reading was meaningless was on screen the whole time.
//
// A fix is not a point. It is a circle: centre `coords`, radius `accuracy`.
// Comparing a circle to a fence has three outcomes, not two, and this module
// returns all three so the caller can say which one happened.
//
// GENERATED — do not edit by hand.
//
// Copied from packages/core/src/geo/geofence.js by tools/sync-geofence.py. Edit the core
// file and re-run that script. `node tools/geofence-conformance.mjs` asserts
// this copy still behaves identically to it.

/** Distance in metres between two GPS points. */
export function haversineDistance(lat1, lon1, lat2, lon2) {
  const toRad = (deg) => (deg * Math.PI) / 180;
  const R = 6371000;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a = Math.sin(dLat / 2) ** 2
    + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/** A fence with no radius set still has to mean something. */
export const DEFAULT_GEOFENCE_RADIUS = 200;

/**
 * How good a fix has to be before it is worth deciding on. Consumer GPS settles
 * to 5-20m outdoors within a few seconds; 30m is reachable in a courtyard and
 * still small against any sane fence.
 */
export const TARGET_ACCURACY_M = 30;

/**
 * A fix this vague says nothing about a 200m fence and is not worth waiting on
 * further — it is a wifi/cell estimate, not a GPS one.
 */
export const USELESS_ACCURACY_M = 1000;

/* -- HOW A FIX IS CAPTURED, which must be identical on both sides ----------
 *
 * THE BUG THESE EXIST TO FIX. The school's centre and the teacher's position
 * are the two halves of one subtraction, and they were being measured by two
 * different routines with two different parameter sets:
 *
 *   the admin screen  seeded with a BALANCED one-shot — a wifi/cell estimate,
 *                     which Android will happily report with a small-looking
 *                     accuracy — then watched for 15s and accepted anything
 *                     whose accuracy was under the fence RADIUS;
 *   the teacher screen went straight to the GPS chip, watched for 8s, and
 *                     accepted whatever it had.
 *
 * Two routines, two windows, two acceptance rules, one subtraction. Standing in
 * the same spot on the same handset, the admin could store a network-derived
 * point and the teacher could then be measured against it from a real satellite
 * fix — and be told they were a kilometre from their own school.
 *
 * So the policy lives here, once, and both clients read it. The capture code
 * still differs by necessity (expo-location on the phone, navigator.geolocation
 * in a browser); the NUMBERS must not.
 */

/** How long to keep improving a reading before answering with the best seen. */
export const FIX_WINDOW_MS = 15000;

/** Precise enough that waiting longer buys nothing. Stops the window early. */
export const GOOD_FIX_METRES = 10;

/**
 * How long a reading may be reused before it has to be taken again.
 *
 * The register is filled in between the reading and the save — forty pupils
 * takes two to four minutes — so the position recorded against it must be where
 * the teacher was when they FILED, not where they were when they picked the
 * subject. Two minutes means the common case does re-measure, which is correct;
 * what it avoids is re-measuring twice within seconds of each other and making
 * the teacher wait through a second window for an answer nobody doubted.
 */
export const FIX_FRESH_MS = 120000;

/**
 * THE FIRST SAMPLE IS NEVER TRUSTED ON ITS OWN.
 *
 * A location provider answers immediately with whatever it has cached — on
 * Android that is routinely a fused network estimate, and its reported accuracy
 * describes the confidence of the *estimate*, not its distance from the truth.
 * Requiring a second sample costs about a second and is what stops a cached
 * wifi fix being written down as a school's surveyed position.
 */
export const MIN_SAMPLES = 2;

/**
 * How accurate a fix must be before it may be stored as a school's CENTRE.
 *
 * Far stricter than the check a teacher's own position gets, and deliberately
 * so: a teacher's bad fix is one bad register, while a bad centre silently
 * moves the fence for every teacher, every day, until somebody notices.
 *
 * `accuracy <= radius` was the old rule and it is not a rule at all — it lets
 * the centre of a 200m fence be 200m wrong, which can put the entire fence off
 * the campus. A quarter of the radius bounds the error to something the fence
 * can absorb, with a 25m floor so a large fence cannot license a sloppy point
 * and a 10m floor below which no consumer GPS reliably goes.
 */
export function centreAccuracyLimit(geofenceRadius) {
  const radius = Number(geofenceRadius) > 0 ? Number(geofenceRadius) : DEFAULT_GEOFENCE_RADIUS;
  return Math.max(25, Math.min(radius / 4, 60));
}

/**
 * A coordinate that a person TYPED, with the provenance of the last detection
 * cleared off it.
 *
 * Editing the latitude by hand used to leave the previous "Use my location"
 * run's `accuracy` and `capturedAt` sitting beside it, so a number somebody read
 * off a map claimed to have been surveyed to ±8m last Tuesday. That is worse
 * than having no provenance at all: every warning built on those fields would
 * confidently vouch for a coordinate nobody measured.
 *
 * `accuracy: null` is the honest answer — the app does not know how right this
 * is — and `source: 'manual'` records why it does not know.
 */
export function typedCoordinate(geo, patch) {
  return {
    ...(geo || {}),
    ...patch,
    accuracy: null,
    capturedAt: new Date().toISOString(),
    source: 'manual',
  };
}

/** Whether a fix is precise enough to be written down as the school's centre. */
export function fixIsUsableAsCentre(accuracyMetres, geofenceRadius) {
  const acc = Number(accuracyMetres);
  if (!Number.isFinite(acc) || acc <= 0) return false;
  return acc <= centreAccuracyLimit(geofenceRadius);
}

/**
 * Where the fix puts someone relative to the fence.
 *
 *   inside     — the whole error circle is within the fence. Certainly there.
 *   outside    — the whole error circle is beyond it. Certainly not there.
 *   uncertain  — the circle straddles the fence. The fix cannot answer.
 *
 * @returns {{verdict:'inside'|'outside'|'uncertain', distance:number, accuracy:number, radius:number}}
 */
export function geofenceVerdict({ distance, accuracy, radius }) {
  const r = Number(radius) > 0 ? Number(radius) : DEFAULT_GEOFENCE_RADIUS;
  const d = Math.max(0, Number(distance) || 0);
  // A missing accuracy is treated as perfect rather than as infinite: some
  // browsers omit it, and assuming the worst there would block everyone.
  const a = Number.isFinite(Number(accuracy)) && Number(accuracy) > 0 ? Number(accuracy) : 0;

  if (d + a <= r) return { verdict: 'inside', distance: d, accuracy: a, radius: r };
  if (d - a > r) return { verdict: 'outside', distance: d, accuracy: a, radius: r };
  return { verdict: 'uncertain', distance: d, accuracy: a, radius: r };
}

/**
 * Whether an attendance submission may proceed.
 *
 * `uncertain` is ALLOWED, deliberately. The alternative is refusing a teacher
 * who is standing in their own classroom because the building has thick walls,
 * and a register that cannot be taken is a worse failure than one taken from a
 * doubtful position — especially as the doubt is recorded either way. Only a
 * fix that puts someone *clearly* outside, error circle and all, is refused.
 */
export function geofenceAllows(verdict) {
  return verdict === 'inside' || verdict === 'uncertain';
}

/**
 * Is this fix good enough to stop waiting for a better one?
 *
 * Settling costs the teacher seconds at the start of every register, so it ends
 * as soon as the reading can actually decide: either it is precise in absolute
 * terms, or it is already unambiguous against this particular fence.
 */
export function fixIsGoodEnough({ accuracy, distance, radius }) {
  const a = Number(accuracy);
  if (!Number.isFinite(a) || a <= 0) return false;
  if (a <= TARGET_ACCURACY_M) return true;
  return geofenceVerdict({ distance, accuracy, radius }).verdict !== 'uncertain';
}

/** The better of two fixes, by accuracy. Used to keep the best of a stream. */
export function betterFix(a, b) {
  if (!a) return b;
  if (!b) return a;
  const aa = Number.isFinite(Number(a.accuracy)) ? Number(a.accuracy) : Infinity;
  const bb = Number.isFinite(Number(b.accuracy)) ? Number(b.accuracy) : Infinity;
  return bb < aa ? b : a;
}
